import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './pc-logo.css.lit';

export const logoVariants = ['base', 'primary'] as const;
export type LogoVariant = (typeof logoVariants)[number];

export const logoColors = ['light', 'dark'] as const;
export type LogoColor = (typeof logoColors)[number];

/**
 * Logo component
 * @element pc-logo
 * @slot - This element has a default slot
 */
@customElement('pc-logo')
export class PCLogo extends OutlineElement {
  static styles: CSSResultArray = [componentStyles];

  @property()
  variant: 'base' | 'primary' = 'base';

  @property()
  color: 'dark' | 'light' = 'light';

  render(): TemplateResult {
    const classes = {
      logo: true,
      [`logo--${this.variant}`]: true,
      [`logo--${this.color}`]: true,
    };

    return html`
      <div class=${classMap(classes)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          id="Layer_2"
          data-name="Layer 2"
          version="1.1"
          viewBox="0 0 388.4 206.5"
        >
          <defs>
            <style>
              .cls-1 {
                fill: inherit;
                stroke-width: 0px;
              }
            </style>
          </defs>
          <path
            class="cls-1"
            d="M243.7,127.2h9.1l.2-1.2c.9-5.5,0-10.3-3-13.8-3.3-3.9-8.7-6-15.7-6-13.7,0-22.9,7.5-25.2,20.5l-2.9,16.4c-1.1,6.2,0,11.5,3.2,15.2,3.3,4,8.8,6.1,15.8,6.1,13.3,0,22.2-7,24.4-19.2l.3-1.6h-9.1l-.2,1.1c-1.3,7.3-6.7,11.4-14.8,11.4s-7.2-1.1-8.9-3.2c-1.7-2.1-2.3-5-1.6-8.9l2.9-16.8c1.4-8.1,7.1-12.7,15.6-12.7s7,1.1,8.7,3.1c1.6,1.8,2,4.5,1.5,7.9l-.3,1.6Z"
          />
          <path
            class="cls-1"
            d="M269.1,164.5c10.8,0,18-5.9,19.8-16.1l2-11.2c.8-4.7,0-8.5-2.4-11.4-2.7-3.2-7.2-4.8-13-4.8-10.9,0-17.9,5.6-19.8,15.8l-2,11.5c-.8,4.8,0,8.6,2.4,11.5,2.7,3.2,7.2,4.8,13,4.8ZM262.4,149l2.2-12.2c.9-5,4.7-7.9,10.5-7.9s5.2.7,6.3,2.1c1,1.2,1.3,3,.9,5.3l-2.1,12c-1,5.3-4.8,8.3-10.6,8.3s-5.1-.7-6.3-2.1c-1-1.2-1.3-3.1-.9-5.4Z"
          />
          <path
            class="cls-1"
            d="M54.8,80.6h12.4c13.8,0,22.3-8.6,22.3-22.3s-2.8-13.9-16.1-13.9h-21.4l-10,56.7h9.2l3.5-20.4ZM56.3,72.3l3.4-19.6h12.6c5.3,0,7.7,2.1,7.7,6.6,0,9.6-6.3,13-12.2,13h-11.6Z"
          />
          <path
            class="cls-1"
            d="M158.4,59.2c-1.5-1.3-5.5-1-5.7-1-2.8,0-7.2,1.3-11,4.1-.4-1.8-2-3.5-3.9-3.5h-3l-7.4,42h8.7l4.6-26.3c1.2-6.2,6.8-8.1,11.3-8s3.3,0,4.8,2.2l1.7-9.6Z"
          />
          <polygon
            class="cls-1"
            points="196.2 101 205 101 212.4 59 203.6 59 196.2 101"
          />
          <path
            class="cls-1"
            d="M220.8,59l-7.1,40.4-.3,1.6h8.8l4.5-25.9c.9-5.4,5.3-8.9,11.1-8.9s4.9.7,6,2c1,1.2,1.3,2.9.9,5.2l-4.8,27.6h8.7l4.7-26.8c.9-4.9.1-8.9-2.3-11.7-2.4-2.8-6.4-4.3-11.5-4.3s-8.5,1.8-11.7,4.2"
          />
          <path
            class="cls-1"
            d="M283.5,73.5h8.7l.2-1.1c.8-4.2.1-7.6-1.9-10-2.3-2.8-6.5-4.2-12.4-4.2-11.3,0-18.2,5-18.8,13.6-.5,5.8,3,9.6,10.1,11.1l7.9,1.6c4.7.9,4.4,3.1,4.2,4.2-.4,3.4-4,5.3-9.9,5.3s-5.5-.6-6.6-1.9c-.8-.9-1-2.3-.7-4.1l.3-1.6h-8.8l-.2,1.2c-.7,4.1,0,7.3,2,9.8,2.5,3,6.9,4.5,13.2,4.5,11.6,0,18.9-5.2,19.5-13.9.4-4-1-9.2-10.2-11.1l-7.9-1.6c-3.3-.7-4.5-1.9-4.2-4.2.4-3.3,3.8-5.1,9.3-5.1s4.9.5,5.8,1.6c.8.9,1,2.3.6,4.4l-.3,1.6Z"
          />
          <path
            class="cls-1"
            d="M175.1,42h-8.5l-10.4,59h8.5l1.9-10.9,6.3-6.5,16.3,28.1c1,1.8,2.9,2.9,5,2.9h6s-20.7-37.7-20.7-37.7l17.6-18h-12.4l-15.5,15.8,5.8-32.8Z"
          />
          <path
            class="cls-1"
            d="M209.5,54.3c-1.5,0-2.7-.5-3.8-1.5-1-1-1.5-2.2-1.4-3.6,0-1.4.6-2.6,1.7-3.6s2.4-1.5,3.9-1.5,2.7.5,3.8,1.5c1,1,1.5,2.2,1.4,3.6,0,1.4-.6,2.6-1.7,3.6-1.1,1-2.4,1.5-3.9,1.5Z"
          />
          <polygon
            class="cls-1"
            points="291.9 163.7 300.7 163.7 308.1 121.7 299.3 121.7 291.9 163.7"
          />
          <path
            class="cls-1"
            d="M305.2,116.4c-1.5,0-2.7-.5-3.8-1.5-1-1-1.5-2.2-1.4-3.6,0-1.4.6-2.6,1.7-3.6s2.4-1.5,3.9-1.5,2.7.5,3.8,1.5,1.5,2.2,1.4,3.6c0,1.4-.6,2.6-1.7,3.6-1.1,1-2.4,1.5-3.9,1.5Z"
          />
          <path
            class="cls-1"
            d="M114.3,88.4c-1.4,3.4-4.9,5.4-9.7,5.4s-5.2-.7-6.4-2.1c-1-1.2-1.4-3.1-1-5.4l.6-3.2h26.4l1.5-8.6c.9-4.7,0-8.5-2.3-11.4-2.7-3.2-7.2-4.9-13-4.9-10.9,0-18,5.6-19.8,15.8l-2,11.5c-.8,4.7,0,8.6,2.5,11.5,2.7,3.2,7.2,4.9,13.1,4.9,10,0,16.8-4.7,19-13.3h-8.9ZM99.4,74.1c.9-5,4.7-7.9,10.6-7.9s5.1.7,6.2,2.1c1,1.2,1.3,3,.9,5.4l-.3,1.9h-17.6l.3-1.5Z"
          />
          <path
            class="cls-1"
            d="M334.6,151.1c-1.4,3.4-4.9,5.4-9.7,5.4s-5.2-.7-6.4-2.1c-1-1.2-1.4-3.1-1-5.4l.6-3.2h26.4l1.5-8.6c.9-4.7,0-8.5-2.3-11.4-2.7-3.2-7.2-4.9-13-4.9-10.9,0-18,5.6-19.8,15.8l-2,11.5c-.8,4.7,0,8.6,2.5,11.5,2.7,3.2,7.2,4.9,13.1,4.9,10,0,16.8-4.7,19-13.3h-8.9ZM319.7,136.8c.9-5,4.7-7.9,10.6-7.9s5.1.7,6.2,2.1c1,1.2,1.3,3,.9,5.4l-.3,1.9h-17.6l.3-1.5Z"
          />
          <path class="cls-1" d="M227.8,62.4c-.4-1.8-2-3.5-3.9-3.5h-3.1" />
        </svg>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-logo': PCLogo;
  }
}
